<template>
  <v-container v-if="!hideForm" fluid class="unauthed-container">
    <v-row class="fill-height" align="center" justify="center">
      <v-col cols="12">
        <v-row align="center" justify="center">
          <div class="signin-form-wrapper">
            <v-card class="signin-form">
              <a href="https://nucla.com" rel="noopener noreferrer">
                <BaseAvatar
                  :src="require('@images/nucla/nucla-logo-light.png')"
                  tile
                  size="64px"
                  class="white-label logo"
                />
              </a>

              <v-card-text class="mt-4">
                <v-form
                  ref="form"
                  class="form-inputs"
                  @submit.prevent="tryToSignIn"
                >
                  <v-text-field
                    v-show="!mfaProcess"
                    v-model="username"
                    solo
                    required
                    hide-details
                    type="text"
                    autocomplete="username"
                    label="Email or username"
                    class="signin-form-field mt-2"
                    @keyup.enter="tryToSignIn"
                  />

                  <v-text-field
                    v-show="!mfaProcess"
                    v-model="password"
                    :append-icon="
                      hidePassword ? 'visibility_off' : 'visibility'
                    "
                    :type="hidePassword ? 'password' : 'text'"
                    solo
                    required
                    hide-details
                    min="8"
                    label="Enter your password"
                    autocomplete="current-password"
                    class="signin-form-field mt-2"
                    @click:append="() => (hidePassword = !hidePassword)"
                    @keyup.enter="tryToSignIn"
                  />

                  <template v-if="mfaProcess">
                    <HelpState :typographic-padding="true">
                      <p class="subtle">
                        We just sent you a temporary signin code. Please check
                        your inbox.
                      </p>
                    </HelpState>

                    <v-text-field
                      v-model="code"
                      solo
                      required
                      hide-details
                      label="Enter temp signin code"
                      class="signin-form-field"
                      @keyup.enter="tryToSignIn"
                    />
                  </template>
                  <div v-if="authError" class="auth-error">{{ authError }}</div>
                </v-form>

                <v-row class="no-padding">
                  <v-col cols="12" class="no-padding">
                    <v-btn
                      :disabled="
                        !username ||
                        !password ||
                        (mfaProcess && !resendMfa && !code)
                      "
                      :loading="loading"
                      class="signin-button"
                      @click="tryToSignIn"
                    >
                      <v-icon color="#4338CA" size="16" class="mr-1"
                        >key</v-icon
                      >
                      {{ signinButtonText }}
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row align="center" justify="center" class="no-padding">
                  <v-col
                    v-if="!mfaProcess"
                    :class="{ 'pr-2': $vuetify.breakpoint.width > 360 }"
                    cols="auto"
                    class="no-padding"
                  >
                    <router-link to="/reset-password" class="reset-password">
                      Forgot Password
                    </router-link>
                  </v-col>

                  <v-col
                    :class="{
                      'pl-2': mfaProcess && $vuetify.breakpoint.width > 360,
                    }"
                    cols="auto"
                    class="no-padding"
                  >
                    <a
                      href="mailto:support@nucla.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="help-link"
                      >Contact Support</a
                    >
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { authMethods } from "@state/helpers"
import { required } from "vuelidate/lib/validators"

export default {
  page: {
    title: "Signin",
  },
  components: {
    HelpState: () => import("@components/core/help-state"),
  },
  data() {
    return {
      username: "",
      password: "",
      code: null,
      authError: "",
      hidePassword: true,
      loading: false,
      mfaProcess: false,
      resendMfa: false,
      hideForm: false,
    }
  },
  computed: {
    signinButtonText() {
      return this.resendMfa ? "Resend Code" : "Login"
    },
  },
  validations: {
    username: {
      required,
    },
    password: {
      required,
    },
  },
  methods: {
    ...authMethods,
    tryToSignIn() {
      if (!this.username || !this.password) {
        return
      }

      this.loading = true
      this.authError = null

      const signinArgs = {
        username: this.username,
        password: this.password,
      }

      // If the MFA process is active and we aren't resending the MFA code...
      if (this.mfaProcess && !this.resendMfa) {
        // Copy/Pasting a value on some devices can add white space... We account for this by removing it.
        signinArgs.code = _.get(this, "code", "").replace(/\s/g, "")
      }

      // If we are resending the MFA verification code then clear the existing one
      if (this.resendMfa) {
        this.code = null
      }

      // Always reset the resend MFA code state. This must be done because the signin funnel all
      // runs through one function so resendMfa is set to true below in this same function.
      this.resendMfa = false

      // Send auth payload to server
      this.signIn(signinArgs)
        .then((resp) => {
          this.hideForm = true
          this.$store.dispatch("auth/loadGroups")
          let redirectFrom = _.get(this.$route, "query.redirectFrom")

          if (redirectFrom && redirectFrom !== "/404") {
            this.$router.push(redirectFrom).catch((e) => {})
          } else {
            this.$store
              .dispatch("auth/getDefaultRoute")
              .then((defaultRoute) => {
                this.$router.push(defaultRoute).catch((e) => {})
              })
          }
        })
        .catch((error) => {
          const errorCode = _.get(error, "response.data.errors[0].code", error)

          // On MFA Error state, exit into MFA processes
          if (errorCode === 8) {
            this.mfaProcess = true

            // Otherwise throw error state for user.
          } else {
            // Conditionally, if the MFA code was just incorrect, trigger the resend process but still throw general error
            if (errorCode === 9) {
              this.resendMfa = true
            }
            this.authError = _.get(
              error,
              "response.data.errors[0].detail",
              error
            )
            return this.authError
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style lang="scss"></style>
